// Lot pre-selection rules for the Fill Product popup.
//
// A product can resolve to multiple NDCs, but a fill must come from a SINGLE
// NDC. These helpers pick which NDC's lots to auto-select and expose the
// currently locked NDC so the UI can prevent mixing NDCs.

export const parseQty = (qty) => parseFloat(qty) || 0;

// The NDC a selection is locked to (the first selected lot's NDC), or null when
// nothing is selected yet.
export const getSelectedNdc = (selectedLots) =>
  Array.isArray(selectedLots) && selectedLots.length > 0 ? selectedLots[0].ndc : null;

// POM-6147 — auto-select exactly ONE lot. A fill draws from a single lot: spreading one
// prescription across several lots made the fill impossible to reconcile against a single
// receipt, and every downstream release path (releaseAndRestockLotsForLineItem) treats a line
// item as one reservation anyway.
//
// Preference order, and the ordering of the first two is deliberate:
//   1. A lot of the DEFAULT NDC that can cover qtyToFill on its own.
//   2. Otherwise the first such lot in NDC order, still one that can cover it alone.
//   3. Only when NO single lot can cover it, the FEFO-first lot of the default NDC (else the
//      first NDC), allocated for what it holds. The caller records the difference as an
//      unverified claim against that same lot.
//
// Step 3 is last on purpose. `unverified` means "dispensed but never received", so minting a
// claim while another lot holds real stock would put a balance on the ledger that nothing can
// ever reconcile — there would be no missing receipt to enter. Preferring a lot that covers the
// fill keeps the bucket honest, at the cost of stepping over an earlier-expiring partial lot.
// That trade is the reason FEFO is not the outermost rule here.
//
// Lots arrive in the order the API returned them (FEFO — earliest expiry first), so "first"
// throughout means earliest-expiring.
export const computeAutoSelectedLots = (items, qtyToFill) => {
  if (!Array.isArray(items) || items.length === 0 || qtyToFill <= 0) return [];

  const inStock = items.filter((lot) => parseQty(lot.remainingQty) > 0);
  if (inStock.length === 0) return [];

  const ndcOrder = [...new Set(inStock.map((lot) => lot.ndc))];
  const defaultNdc = inStock.find((lot) => lot.isDefault)?.ndc;

  // The earliest-expiring lot of one NDC that can cover the whole fill by itself.
  const coveringLotFor = (ndc) =>
    inStock.find((lot) => lot.ndc === ndc && parseQty(lot.remainingQty) >= qtyToFill);

  let chosen = defaultNdc ? coveringLotFor(defaultNdc) : undefined;
  if (!chosen) chosen = ndcOrder.map(coveringLotFor).find(Boolean);
  if (!chosen) {
    // Nothing can cover it alone: start the operator on the preferred NDC's first lot and let
    // the dialog say what happens to the remainder.
    const fallbackNdc = defaultNdc ?? ndcOrder[0];
    chosen = inStock.find((lot) => lot.ndc === fallbackNdc);
  }
  if (!chosen) return [];

  return [{ ...chosen, qtyToAllocate: Math.min(parseQty(chosen.remainingQty), qtyToFill) }];
};

// POM-6083 — manually entered lots.
//
// Everything in the dialog is keyed on inventoryLotId: the selected-lot map, the
// remove and quantity handlers, the row keys. A manually entered lot has no id
// yet, so it needs a stable stand-in for the life of the dialog. `lotKey` is the
// single place that decides, so no call site has to remember the fallback.
export const lotKey = (lot) => lot?.inventoryLotId ?? lot?.manualKey ?? null;

// A manual row is one the operator recorded by hand, whether they typed a brand new
// lot number or picked one of the product's zero-quantity lots. It is flagged
// explicitly rather than inferred from a missing inventoryLotId: since POM-6083 the
// panel can produce a row that HAS an id (the operator claimed a real drained row),
// and that row still belongs in the unverified section, not the FEFO list.
export const isManualLot = (lot) => !!lot && (lot.recordedManually === true || !lot.inventoryLotId);

// Identity of a hand-recorded lot for the duplicate guard. Not the React key - see lotKey.
// Lot numbers are compared case-INSENSITIVELY, matching the in-stock collision check in
// FillProductLotDialog. Case-sensitive, adding ABC123 and then abc123 for the same NDC and
// BUD slipped past this guard and posted the claim twice for one physical lot — the exact
// double-post it exists to stop.
const manualIdentity = ({ lotNumber, ndc, expirationDate }) =>
  `manual:${(ndc ?? '').toLowerCase()}:${(lotNumber ?? '').toLowerCase()}:${expirationDate ?? ''}`;

export const buildManualLot = ({
  lotNumber,
  ndc,
  expirationDate,
  quantity,
  // POM-6083 - set when the operator picked an existing zero-quantity lot from the
  // dropdown instead of typing a new number. Claiming the real row is what stops one
  // physical lot being split across two rows, with the negative on one and the
  // received history on the other.
  inventoryLotId = null,
  // POM-6083 - true when the operator read a BUD off the vial for a PICKED lot that had none.
  // Distinguished from an ordinary BUD because it has to reach the API alongside a real lot id,
  // where the value is otherwise ignored.
  suppliesBud = false,
} = {}) => ({
  manualKey: manualIdentity({ lotNumber, ndc, expirationDate }),
  inventoryLotId: inventoryLotId ?? null,
  lotNumber,
  ndc,
  // Empty string from a cleared date field must become null, not ''. The API
  // treats null as "no BUD supplied" and '' would fail date parsing server-side.
  expirationDate: expirationDate || null,
  // Only a typed lot is new. A picked row already exists, so claiming it must not ask
  // the back end to resolve-or-create anything.
  isNewLot: !inventoryLotId,
  suppliesBud,
  recordedManually: true,
  // A hand-recorded lot has no dispensable stock, so nothing constrains the quantity
  // except what the fill still needs. remainingQty is what the qty clamp reads, so it
  // has to be the requested quantity rather than 0 - at 0 the clamp would pin every
  // such row to zero and the fill could never be allocated.
  remainingQty: quantity,
  qtyToAllocate: quantity,
});

// Duplicate guard for the selected list. Entering the same claim twice would post the
// quantity to the unverified bucket twice over. A picked row is matched on its real id;
// a typed one on (NDC, lot number, BUD), so the same lot number under a different BUD -
// legitimate, a lot repeating across shipments - is still allowed.
export const hasManualLot = (selectedLots, { lotNumber, ndc, expirationDate, inventoryLotId }) => {
  const lots = selectedLots ?? [];
  if (inventoryLotId) return lots.some((lot) => lot.inventoryLotId === inventoryLotId);
  const key = manualIdentity({ lotNumber, ndc, expirationDate });
  return lots.some((lot) => lot.manualKey === key);
};
